import { logger } from "../../core/Logger.js";
/**
 * @class DataCollector
 * @description Collecte exhaustive des métriques comportementales pour l'IA.
 * Capture non seulement "quoi", mais aussi "comment" Yasso joue.
 */
export class DataCollector {
    constructor() {
        this.reset();
        this.threshold = 40; // Seuil ajusté pour une analyse plus fine
    }

    reset() {
        this.data = {
            // --- MOBILITÉ ---
            movements: { left: 0, right: 0, up: 0, down: 0 },
            dash: {
                count: 0,
                lastTime: 0,
                averageInterval: 0, // Détecte le spam de dash
                distanceTotal: 0
            },

            // --- COMBAT & AGRESSION ---
            combat: {
                enemiesKilled: 0,
                enemiesSkipped: 0,
                damageTaken: 0,
                shotsFired: 0, // Si applicable plus tard
                dashKills: 0,  // Kills techniques
                lastKillTime: 0,
                killStreak: 0  // Détecte les phases d'agression intense
            },

            // --- PERFORMANCE ---
            session: {
                startTime: Date.now(),
                roomsCleared: 0,
                healthAtRoomStart: 1.0,
                powerUpsCollected: 0
            }
        };

        this.actionCounter = 0;
        this.lastMoveTime = 0;
    }

    /**
     * Enregistre un mouvement avec calcul de fréquence.
     * Optimisé : throttling temporel + échantillonnage (1 mouvement sur 10)
     */
    recordMove(dir) {
        const now = Date.now();
        if (now - this.lastMoveTime < 100) return;

        // Throttling supplémentaire : On n'enregistre qu'une fraction des mouvements
        // pour éviter de saturer les compteurs si le joueur bouge en continu.
        this._moveThrottleCount = (this._moveThrottleCount || 0) + 1;
        if (this._moveThrottleCount < 10) return; 
        this._moveThrottleCount = 0;

        if (this.data.movements[dir] !== undefined) {
            this.data.movements[dir]++;
            this.actionCounter += 0.5; // Le mouvement pèse moins que le combat
            this.lastMoveTime = now;
        }
    }

    /**
     * Enregistre un dash et calcule le rythme (Spam vs Précision).
     */
    recordDash() {
        const now = Date.now();
        const interval = now - this.data.dash.lastTime;

        // Calcul de la moyenne glissante de l'intervalle entre les dashs
        this.data.dash.averageInterval = (this.data.dash.averageInterval + interval) / 2;

        this.data.dash.count++;
        this.data.dash.lastTime = now;
        this.actionCounter += 2;
    }

    /**
     * Enregistre un kill et gère le multiplicateur d'agression.
     */
    recordKill(isDashKill = false) {
        const now = Date.now();

        // Gestion du streak (Kills à moins de 3 secondes)
        if (now - this.data.combat.lastKillTime < 3000) {
            this.data.combat.killStreak++;
        } else {
            this.data.combat.killStreak = 1;
        }

        this.data.combat.enemiesKilled++;
        this.data.combat.lastKillTime = now;

        // ✅ FORCE LE COMPTAGE TECHNIQUE
        if (isDashKill === true) {
            this.data.combat.dashKills++;
            this.actionCounter += 7; // Les dash kills font progresser l'IA plus vite
        } else {
            this.actionCounter += 4;
        }
    }

    /**
     * Enregistre les dégâts reçus pour le profil de vulnérabilité.
     */
    recordDamage(amount) {
        this.data.combat.damageTaken += amount;
        this.actionCounter += 3; // L'IA doit réagir si le joueur prend cher
    }

    recordBonusCollected() {
        this.data.session.powerUpsCollected++;
        this.actionCounter += 5;
    }

    /**
     * Analyseur d'agression complexe.
     * @returns {number} 0 (Passif/Lent) à 1 (Elite/Hyper-agressif)
     */
    getAggressionLevel() {
        const totalEncountered = this.data.combat.enemiesKilled + this.data.combat.enemiesSkipped;
        if (totalEncountered === 0) return 0.5;

        const killRatio = this.data.combat.enemiesKilled / totalEncountered;
        const streakBonus = Math.min(this.data.combat.killStreak / 5, 0.5);

        return Math.min(killRatio + streakBonus, 1.0);
    }

    /**
     * Analyseur d'esquive / passivité.
     * @returns {number} 0 (Agressif) à 1 (Très passif/esquiveur)
     */
    getEvasionLevel() {
        const kills  = this.data.combat.enemiesKilled;
        const dashes = this.data.dash.count;

        // Beaucoup de dashs sans kills = joueur qui esquive
        if (dashes === 0) return 0.2;
        const killsPerDash = kills / dashes;
        // 0 kill/dash → 1.0 (pure esquive), 1+ kill/dash → 0.0 (agressif)
        return Math.max(0, Math.min(1.0, 1.0 - killsPerDash));
    }

    /**
     * Multiplicateur de points de vie des ennemis.
     * Plus le joueur attaque (agressif), plus les ennemis sont robustes.
     */
    get hpMult() {
        const aggression = this.getAggressionLevel();
        // Base 1.0, monte jusqu'à 1.5 si très agressif
        return 1.0 + (aggression * 0.5);
    }

    /**
     * Multiplicateur du budget d'ennemis (nombre d'ennemis).
     * Plus le joueur esquive, plus il y a d'ennemis.
     */
    get budgetMult() {
        const evasion = this.getEvasionLevel();
        // Base 1.0, monte jusqu'à 1.5 si très fuyard
        return 1.0 + (evasion * 0.5);
    }

    shouldAdapt() {
        if (this.actionCounter >= this.threshold) {
            this.actionCounter = 0;
            return true;
        }
        return false;
    }

    /**
     * Méthode de secours pour la compatibilité descendante
     */
    recordAction(type) {
        if (type === "dash_kill") {
            this.recordKill(true);
        } else if (type === "kills") {
            this.recordKill(false);
        } else {
            this.actionCounter++;
        }
    }

    /**
     * Enregistre la complétion d'une salle et analyse les ennemis restants.
     * @param {number} remainingEnemies - Nombre d'ennemis ignorés/vivants
     */
    recordRoomCompletion(remainingEnemies = 0) {
        this.data.session.roomsCleared++;
        this.data.combat.enemiesSkipped += remainingEnemies;

        // Un bonus d'action pour avoir terminé la salle
        this.actionCounter += 10;

        logger.info("AI", "DEBUG", `📊 IA : Salle complétée. Total: ${this.data.session.roomsCleared}`);
    }
}